import { type ICloudinaryApi } from './ICloudinaryApi';
import { CloudinaryUploadRequestDTO, CloudinaryUploadResponseDTO } from 'infrastructure/dtos/media';

export class CloudinaryApi implements ICloudinaryApi {
  private readonly cloudName = 'dvgbcmpmk';
  private readonly uploadUrl = `https://api.cloudinary.com/v1_1/${this.cloudName}/auto/upload`;

  private async uploadToCloudinary(
    dto: CloudinaryUploadRequestDTO
  ): Promise<CloudinaryUploadResponseDTO> {
    const formData = new FormData();
    formData.append('file', dto.file);
    formData.append('upload_preset', dto.uploadPreset);

    if (dto.publicId) {
      formData.append('public_id', dto.publicId);
    }

    if (dto.folder) {
      formData.append('folder', dto.folder);
    }

    try {
      const response = await fetch(this.uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Cloudinary upload failed: ${errorData.error?.message || response.statusText}`
        );
      }

      const result = await response.json();

      return {
        publicId: result.public_id,
        secureUrl: result.secure_url,
        url: result.url,
        assetId: result.asset_id,
        version: result.version,
        versionId: result.version_id,
        signature: result.signature,
        width: result.width,
        height: result.height,
        format: result.format,
        resourceType: result.resource_type,
        createdAt: result.created_at,
        bytes: result.bytes,
        etag: result.etag,
        placeholder: result.placeholder,
        originalFilename: result.original_filename,
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to upload to Cloudinary: ${error.message}`);
      }
      throw new Error(`Failed to upload to Cloudinary: ${error}`);
    }
  }

  public async uploadImage(dto: CloudinaryUploadRequestDTO): Promise<CloudinaryUploadResponseDTO> {
    // Validate that it's an image
    if (!dto.file.type || !dto.file.type.startsWith('image/')) {
      const fileName = dto.file instanceof File ? dto.file.name : 'blob';
      console.warn('File type validation failed:', {
        fileName: fileName,
        fileType: dto.file.type,
        fileSize: dto.file.size,
      });

      // Try to detect image type from file extension as fallback (only for File objects)
      if (dto.file instanceof File) {
        const fileNameLower = fileName.toLowerCase();
        const isImageByExtension = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/.test(fileNameLower);

        if (!isImageByExtension) {
          throw new Error(
            `The provided file is not an image. File type: ${dto.file.type || 'unknown'}, File name: ${fileName}`
          );
        }

        console.log('File validated as image by extension, proceeding with upload...');
      } else {
        throw new Error(
          `The provided blob is not an image. Blob type: ${dto.file.type || 'unknown'}`
        );
      }
    }

    // Ensure correct upload preset for images
    if (dto.uploadPreset !== 'instagram_pictures') {
      throw new Error('Images must use the instagram_pictures upload preset');
    }

    return await this.uploadToCloudinary(dto);
  }

  public async uploadVideo(dto: CloudinaryUploadRequestDTO): Promise<CloudinaryUploadResponseDTO> {
    // Validate that it's a video
    if (!dto.file.type || !dto.file.type.startsWith('video/')) {
      const fileName = dto.file instanceof File ? dto.file.name : 'blob';
      console.warn('File type validation failed:', {
        fileName: fileName,
        fileType: dto.file.type,
        fileSize: dto.file.size,
      });

      // Try to detect video type from file extension as fallback (only for File objects)
      if (dto.file instanceof File) {
        const fileNameLower = fileName.toLowerCase();
        const isVideoByExtension = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv|m4v|3gp)$/.test(
          fileNameLower
        );

        if (!isVideoByExtension) {
          throw new Error(
            `The provided file is not a video. File type: ${dto.file.type || 'unknown'}, File name: ${fileName}`
          );
        }

        console.log('File validated as video by extension, proceeding with upload...');
      } else {
        throw new Error(
          `The provided blob is not a video. Blob type: ${dto.file.type || 'unknown'}`
        );
      }
    }

    // Ensure correct upload preset for videos
    if (dto.uploadPreset !== 'instagram_videos') {
      throw new Error('Videos must use the instagram_videos upload preset');
    }

    return await this.uploadToCloudinary(dto);
  }
}
